import React from "react";
import { Button, Box, styled, CircularProgress, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { colors } from "../../theme/colors";
import { TreatmentPlanListItem } from "../../services/treatmentPlanService";
import { RightArrowIcon } from "../Icons";

const ContentContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  minWidth: "280px",
});

const Title = styled("h3")({
  fontFamily: "'Sora', sans-serif",
  fontSize: "18px",
  fontWeight: 600,
  color: colors.text.primary,
  margin: "0 0 8px 0",
  textAlign: "center",
});

const PlanButton = styled(Button)({
  justifyContent: "space-between",
  padding: "12px 16px",
  backgroundColor: "#E8F6F9",
  color: "#0E426D",
  border: "none",
  fontFamily: "'Nunito', sans-serif",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  textDecoration: "underline",
  "&:hover": {
    backgroundColor: "#d9eff4",
    border: "none",
    textDecoration: "underline",
  },
});

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
});

const EmptyText = styled("p")({
  fontFamily: "'Nunito', sans-serif",
  fontSize: "14px",
  color: colors.text.secondary,
  textAlign: "center",
  margin: 0,
});

interface TreatmentPlanSelectPopoverContentProps {
  plans: TreatmentPlanListItem[];
  loading?: boolean;
  onSelect: (planId: number) => void;
}

const TreatmentPlanSelectPopoverContent: React.FC<
  TreatmentPlanSelectPopoverContentProps
> = ({ plans, loading = false, onSelect }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <ContentContainer>
        <Title>{t("treatmentPlanSelect.title")}</Title>
        <LoadingContainer>
          <CircularProgress size={24} />
        </LoadingContainer>
      </ContentContainer>
    );
  }

  if (plans.length === 0) {
    return (
      <ContentContainer>
        <Title>{t("treatmentPlanSelect.title")}</Title>
        <EmptyText>{t("treatmentPlanSelect.empty")}</EmptyText>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer>
      <Title>{t("treatmentPlanSelect.title")}</Title>
      {plans.map((plan) => (
        <Tooltip key={plan.id} title={plan.name} placement="left" arrow>
          <PlanButton
            variant="contained"
            fullWidth
            onClick={() => onSelect(plan.id)}
            size="small"
            endIcon={
              <RightArrowIcon width="20px" height="20px" fill="#0E426D" />
            }
          >
            {plan.name.length > 25
              ? `${plan.name.substring(0, 25)}....`
              : plan.name}
          </PlanButton>
        </Tooltip>
      ))}
    </ContentContainer>
  );
};

export default TreatmentPlanSelectPopoverContent;
